import { useMemo } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { ChevronDown, X } from 'lucide-react'
import { useSupabaseClient } from 'src/api/supabase'
import { queryKeys } from 'src/api/db/queryKeys'
import { Combobox } from 'src/shared/components/Combobox'
import { Column, Row } from 'src/shared/components/Semantic/all'
import { Button } from 'src/shared/components/ui/button'
import { Card } from 'src/shared/components/ui/card'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from 'src/shared/components/ui/collapsible'
import { Label } from 'src/shared/components/ui/label'

export const DecorationInfoAssociator = ({
  printCode,
}: {
  printCode: string
}) => {
  const supabase = useSupabaseClient()
  const queryClient = useQueryClient()

  const { data: allInfo } = useQuery({
    queryKey: [queryKeys.decorations, 'allDecorationInfo'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('decoration_info')
        .select('id, name, print_codes')
        .order('name', { ascending: true })
      if (error) throw error
      return data ?? []
    },
  })

  const associated = useMemo(
    () =>
      (allInfo ?? []).filter((info) => info.print_codes?.includes(printCode)),
    [allInfo, printCode]
  )

  const unassociated = useMemo(
    () =>
      (allInfo ?? []).filter((info) => !info.print_codes?.includes(printCode)),
    [allInfo, printCode]
  )

  const options = useMemo(
    () =>
      unassociated.map((info) => ({
        value: String(info.id),
        label: info.name ?? `ID ${info.id}`,
      })),
    [unassociated]
  )

  const updatePrintCodes = useMutation({
    mutationFn: async ({
      id,
      print_codes,
    }: {
      id: number
      print_codes: string[]
    }) => {
      const { error } = await supabase
        .from('decoration_info')
        .update({ print_codes })
        .eq('id', id)
      if (error) throw error
    },
    onSuccess: () =>
      queryClient.invalidateQueries({ queryKey: [queryKeys.decorations] }),
  })

  const associate = (infoId: number) => {
    const info = allInfo?.find((i) => i.id === infoId)
    if (!info) return
    const updated = [...(info.print_codes ?? []), printCode]
    updatePrintCodes.mutate({ id: infoId, print_codes: updated })
  }

  const disassociate = (infoId: number) => {
    const info = allInfo?.find((i) => i.id === infoId)
    if (!info) return
    const updated = (info.print_codes ?? []).filter((c) => c !== printCode)
    updatePrintCodes.mutate({ id: infoId, print_codes: updated })
  }

  return (
    <Card>
      <Collapsible defaultOpen>
        <CollapsibleTrigger asChild>
          <Button variant="ghost" className="w-full justify-between px-4 py-2">
            <span className="font-medium">Decoration info</span>
            <ChevronDown className="h-4 w-4" />
          </Button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <Column className="gap-3 p-4 pt-2">
            {associated.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No decoration info associated with this print code
              </p>
            ) : (
              <Column className="gap-2">
                {associated.map((info) => (
                  <Row
                    key={info.id}
                    className="items-center justify-between rounded-md border px-3 py-2"
                  >
                    <span className="text-sm">
                      {info.name ?? `ID ${info.id}`}
                    </span>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      onClick={() => disassociate(info.id)}
                      disabled={updatePrintCodes.isPending}
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  </Row>
                ))}
              </Column>
            )}

            <Column className="gap-1">
              <Label className="text-xs text-foreground/60 ml-1">
                Associate decoration info
              </Label>
              <Combobox
                value=""
                onValueChange={(next) => associate(Number(next))}
                options={options}
                placeholder="Select decoration info..."
                searchPlaceholder="Search..."
                emptyMessage="No decoration info found."
                disabled={updatePrintCodes.isPending}
                buttonClassName="w-[300px] justify-between font-normal"
              />
            </Column>
          </Column>
        </CollapsibleContent>
      </Collapsible>
    </Card>
  )
}
